<template>
  <div class="cl-panel-stack">
    <article class="card">
      <header class="cl-card-head">
        <div>
          <p class="eyebrow">Structure</p>
          <h3>Runs and encounters</h3>
          <p class="muted-line">
            Detected from CHALLENGE_MODE_START/END and ENCOUNTER_START/END. Picking one here is the same as picking it in the Fight list above.
          </p>
        </div>
      </header>

      <ul class="cl-segment-list">
        <li
          v-for="segment in segments"
          :key="segment.key"
          :class="{ 'is-selected': segment.key === selectedSegmentKey }"
        >
          <button type="button" class="cl-segment-main" @click="$emit('selectSegment', segment.key)">
            <span class="cl-segment-title">
              <i class="fa-solid" :class="iconFor(segment)"></i>
              <strong>{{ segment.name }}</strong>
              <span v-if="keystoneLabel(segment)" class="cl-chip is-key">{{ keystoneLabel(segment) }}</span>
              <span v-if="segment.meta && segment.meta.difficulty" class="cl-chip">{{ segment.meta.difficulty }}</span>
              <span class="cl-chip" :class="resultClass(segment)">{{ resultLabel(segment) }}</span>
            </span>
            <span class="cl-segment-meta">
              <span><i class="fa-regular fa-clock"></i> {{ formatClock(segment.startTs) }}</span>
              <span><i class="fa-solid fa-stopwatch"></i> {{ formatDuration(segment.durationMs) }}</span>
              <span v-if="segment.fightMs"><i class="fa-solid fa-hourglass-half"></i> {{ formatDuration(segment.fightMs) }} fight time</span>
              <span><i class="fa-solid fa-users"></i> {{ segment.playerCount }} players</span>
              <span><i class="fa-solid fa-skull"></i> {{ plural(segment.deathCount, 'death') }}</span>
              <span v-if="segment.totals"><i class="fa-solid fa-bolt"></i> {{ formatCompact(segment.totals.damage) }} damage</span>
            </span>
            <span v-if="affixNames(segment).length" class="cl-segment-affixes">
              <span v-for="affix in affixNames(segment)" :key="affix" class="cl-chip is-subtle">{{ affix }}</span>
            </span>
          </button>
        </li>
      </ul>
    </article>

    <article v-if="keystone" class="card">
      <header class="cl-card-head">
        <div>
          <p class="eyebrow">Mythic+</p>
          <h3>{{ keystone.name }} +{{ keystone.meta.keystoneLevel }}</h3>
          <p class="muted-line">Only values present in the log are shown; anything the log does not record stays empty.</p>
        </div>
      </header>
      <dl class="cl-definition-list">
        <div>
          <dt>Keystone level</dt>
          <dd>{{ keystone.meta.keystoneLevel || NO_DATA }}</dd>
        </div>
        <div>
          <dt>Result</dt>
          <dd>{{ resultLabel(keystone) }}</dd>
        </div>
        <div>
          <dt>Clear time (log)</dt>
          <dd>{{ keystone.meta.totalTimeMs ? formatDuration(keystone.meta.totalTimeMs) : NO_DATA }}</dd>
        </div>
        <div>
          <dt>Wall clock duration</dt>
          <dd>{{ formatDuration(keystone.durationMs) }}</dd>
        </div>
        <div>
          <dt>Dungeon timer</dt>
          <dd>{{ keystoneTimerMs ? formatDuration(keystoneTimerMs) : NO_DATA }}</dd>
        </div>
        <div>
          <dt>Upgrades</dt>
          <dd>{{ upgradeLabel }}</dd>
        </div>
        <div>
          <dt>Run rating</dt>
          <dd>{{ keystone.meta.runRating ? formatNumber(keystone.meta.runRating) : NO_DATA }}</dd>
        </div>
        <div>
          <dt>Affixes</dt>
          <dd>{{ affixNames(keystone).join(', ') || NO_DATA }}</dd>
        </div>
        <div>
          <dt>Deaths</dt>
          <dd>{{ keystone.deathCount }}</dd>
        </div>
        <div>
          <dt>Challenge mode id</dt>
          <dd>{{ keystone.meta.challengeModeId || NO_DATA }}</dd>
        </div>
      </dl>
      <p v-if="keystone.meta.abandoned" class="cl-warn-text">
        The log ends without a completed CHALLENGE_MODE_END for this run, so success or failure cannot be determined.
      </p>
    </article>

    <article v-if="bosses.length" class="card">
      <header class="cl-card-head">
        <div>
          <p class="eyebrow">Bosses</p>
          <h3>Encounter results</h3>
        </div>
      </header>
      <div class="cl-table-scroll">
        <table class="cl-table">
          <thead>
            <tr>
              <th>Boss</th>
              <th>Difficulty</th>
              <th>Start</th>
              <th class="is-numeric">Fight time</th>
              <th class="is-numeric">Damage</th>
              <th class="is-numeric">Healing</th>
              <th class="is-numeric">Deaths</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="boss in bosses"
              :key="boss.key"
              :class="{ 'is-selected': boss.key === selectedSegmentKey }"
              @click="$emit('selectSegment', boss.key)"
            >
              <td>{{ boss.name }}</td>
              <td>{{ (boss.meta && boss.meta.difficulty) || NO_DATA }}</td>
              <td>{{ formatClock(boss.startTs) }}</td>
              <td class="is-numeric">{{ formatDuration(boss.fightMs || boss.durationMs) }}</td>
              <td class="is-numeric">{{ formatCompact(boss.totals.damage) }}</td>
              <td class="is-numeric">{{ formatCompact(boss.totals.healing) }}</td>
              <td class="is-numeric">{{ boss.deathCount }}</td>
              <td>
                <span class="cl-chip" :class="resultClass(boss)">{{ resultLabel(boss) }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </article>
  </div>
</template>

<script>
import { describeAffix } from '../../services/combatLog/constants'
import {
  NO_DATA,
  formatNumber,
  formatCompact,
  formatDuration,
  formatClock,
  plural,
} from '../../services/combatLog/format'

export default {
  name: 'CombatLogEncounters',
  props: {
    summary: { type: Object, required: true },
    selectedSegmentKey: { type: String, default: 'log' },
    // Taken from Raider.IO when a dungeon could be matched; the log itself does
    // not contain the timer.
    dungeonIndex: { type: Object, default: null },
  },
  emits: ['selectSegment'],
  data() {
    return { NO_DATA }
  },
  computed: {
    // Newest first, the way the fight picker and the log list read; the whole
    // log spans all of them, so it stays above them.
    segments() {
      return [...(this.summary.segments || [])].sort((left, right) => {
        if (left.type === 'log') return -1
        if (right.type === 'log') return 1
        return right.startTs - left.startTs
      })
    },
    bosses() {
      return this.segments.filter((segment) => segment.type === 'encounter')
    },
    keystone() {
      const selected = this.segments.find((segment) => segment.key === this.selectedSegmentKey)
      if (selected && selected.type === 'challenge') return selected
      // The list is newest first, so the latest run is the one at its head.
      const runs = this.segments.filter((segment) => segment.type === 'challenge')
      return runs.length ? runs[0] : null
    },
    keystoneTimerMs() {
      if (!this.keystone || !this.dungeonIndex) return 0
      const entry = this.dungeonIndex[this.keystone.meta.challengeModeId]
      return entry ? entry.keystoneTimerMs : 0
    },
    upgradeLabel() {
      const run = this.keystone
      if (!run || !run.meta.totalTimeMs || !this.keystoneTimerMs) return NO_DATA
      if (!run.meta.success) return 'Not timed'
      const ratio = run.meta.totalTimeMs / this.keystoneTimerMs
      if (ratio <= 0.6) return '+3 (under 60% of the timer)'
      if (ratio <= 0.8) return '+2 (under 80% of the timer)'
      if (ratio <= 1) return '+1 (inside the timer)'
      return 'Completed over time'
    },
  },
  methods: {
    formatNumber,
    formatCompact,
    formatDuration,
    formatClock,
    plural,
    iconFor(segment) {
      if (segment.type === 'challenge') return 'fa-dungeon'
      if (segment.type === 'encounter') return 'fa-dragon'
      return 'fa-file-lines'
    },
    keystoneLabel(segment) {
      if (!segment.meta || !segment.meta.keystoneLevel) return ''
      return `+${segment.meta.keystoneLevel}`
    },
    resultLabel(segment) {
      const meta = segment.meta || {}
      if (segment.type === 'log') return 'Whole log'
      if (meta.abandoned) return NO_DATA
      if (meta.success === true) return segment.type === 'challenge' ? 'Timed' : 'Kill'
      if (meta.success === false) return segment.type === 'challenge' ? 'Over time' : 'Wipe'
      if (meta.completed === false) return 'Incomplete'
      return NO_DATA
    },
    resultClass(segment) {
      const meta = segment.meta || {}
      if (meta.success === true) return 'is-good'
      if (meta.success === false) return 'is-bad'
      return 'is-subtle'
    },
    affixNames(segment) {
      const ids = (segment.meta && segment.meta.affixIds) || []
      return ids.map((id) => describeAffix(id))
    },
  },
}
</script>
